import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import axios, { AxiosInstance } from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import { from, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
function asArray<T>(value: T | T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

export interface DeepLTranslationResponse {
  translations: Array<{
    detected_source_language: string;
    text: string;
  }>;
}
@Injectable({
  providedIn: 'root',
})
export class ClimatiqService {
  private lambdaClimatiqUrls = environment.lambdaUrls.climatiq;
  private deeplAxios: AxiosInstance;
  private readonly deeplProxyUrl =
    'https://46h6mw35g6.execute-api.eu-north-1.amazonaws.com/translate';

  constructor() {
    this.deeplAxios = axios.create({ baseURL: this.deeplProxyUrl });
  }

  private async proxyPost(path: string, data: any): Promise<any> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(`${this.lambdaClimatiqUrls}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `Climatiq proxy error ${response.status}`);
    }
    return response.json();
  }
  travelEstimate = async (data: any): Promise<any> => {
    return this.proxyPost('travel', data);
  };

  estimate = async (data: any): Promise<any> => {
    return this.proxyPost('estimate', data);
  };

  spendEstimate = async (data: any): Promise<any> => {
    return this.proxyPost('spend', data);
  };

  suggestData = async (data: any): Promise<any> => {
    return this.proxyPost('suggest', data);
  };
  emissions = async (
    categories: any,
    currentCategory: number,
    currentQuestion: number,
  ) => {
    if (currentCategory === 0) {
      if (currentQuestion === 0) {
        let promises: any = [];
        let payload: any = null;

        payload = async (volume_m3: number = 0) => {
          // Italian natural gas LHV (ISPRA 2023 national inventory): 34.01 MJ/m³ ÷ 3.6 = 9.45 kWh/m³
          // No IT combustion factor exists in Climatiq; DE/Bafa with LHV conversion gives -3.1% vs ISPRA target
          const kwh = Number(volume_m3) * 9.45;
          return await this.estimate({
            emission_factor: {
              activity_id: 'fuel-type_natural_gas-fuel_use_na',
              data_version: '^0',
              region: 'DE',
            },
            parameters: {
              energy: kwh,
              energy_unit: 'kWh',
            },
          });
        };

        const question = categories[0].questions[0];

        Object.keys(question.answers ?? {}).forEach((key) => {
          if (key.length) {
            promises.push(payload(question.answers[key])); // answers are in m³
          }
        });

        const data = await Promise.all(promises);

        question.emissions = data;
      }
      if (currentQuestion === 1) {
        let promises: any = [];
        let payload: any = null;

        payload = async (row: any) => {
          let activity_id = '';
          let density = 0; // kg per liter

          if (row.fuel === 'Gasoline' || row.fuel === 'Benzina') {
            activity_id = 'fuel-type_motor_gasoline-fuel_use_na';
            density = 0.74;
          }

          if (row.fuel === 'Diesel') {
            activity_id = 'fuel-type_gas_diesel_oil-fuel_use_na';
            density = 0.832;
          }

          if (row.fuel === 'Fuel Oil' || row.fuel === 'Olio combustibile') {
            activity_id = 'fuel-type_fuel_oil-fuel_use_na';
            density = 0.96;
          }

          return await this.estimate({
            emission_factor: {
              activity_id,
              data_version: '^20',
            },
            parameters: {
              weight: (Number(row.liters) ?? 0) * density,
              weight_unit: 'kg',
            },
          });
        };

        const question = categories[0].questions[1];

        Object.keys(question.answers ?? {}).forEach((key) => {
          if (key.length) {
            promises.push(payload(question.answers[key]));
          }
        });

        const data = await Promise.all(promises);

        question.emissions = data;
      }
      if (currentQuestion === 2) {
        let promises: any = [];
        let payload: any = null;

        payload = async (row: any) => {
          let activity_id = '';
          let density = 0;

          if (row.fuel === 'petrol') {
            activity_id = 'fuel-type_motor_gasoline-fuel_use_na';
            density = 0.74;
          }

          if (row.fuel === 'diesel') {
            activity_id = 'fuel-type_gas_diesel_oil-fuel_use_na';
            density = 0.832;
          }

          if (row.fuel === 'electric') {
            return await this.estimate({
              emission_factor: {
                activity_id: 'electricity-supply_grid-source_production_mix',
                data_version: '^20',
                region: 'IT',
              },
              parameters: {
                energy: Number(row.kwh) ?? 0,
                energy_unit: 'kWh',
              },
            });
          }

          return await this.estimate({
            emission_factor: {
              activity_id,
              data_version: '^20',
            },
            parameters: {
              weight: (Number(row.liters) ?? 0) * density,
              weight_unit: 'kg',
            },
          });
        };

        const question = categories[0].questions[2];

        Object.keys(question.answers ?? {}).forEach((key) => {
          if (key.length) {
            promises.push(payload(question.answers[key]));
          }
        });

        const data = await Promise.all(promises);

        question.emissions = data;
      }
      if (currentQuestion === 3) {
        const question = categories[0]?.questions[3];
        const data = question?.answers;
        if (!data || !data.length) return;

        let promises: any = [];

        const payload = async (row: any) => {
          let activity_id = '';
          let density = 0;

          if (row.fuel === 'petrol') {
            activity_id = 'fuel-type_motor_gasoline-fuel_use_na';
            density = 0.74;
          } else if (row.fuel === 'diesel') {
            activity_id = 'fuel-type_gas_diesel_oil-fuel_use_na';
            density = 0.832;
          } else if (row.fuel === 'electric' || row.fuel === 'hybrid') {
            return await this.estimate({
              emission_factor: {
                activity_id: 'electricity-supply_grid-source_production_mix',
                data_version: '^20',
                region: 'IT',
              },
              parameters: {
                energy: Number(row.kwh) ?? 0,
                energy_unit: 'kWh',
              },
            });
          }

          return await this.estimate({
            emission_factor: { activity_id, data_version: '^20' },
            parameters: {
              weight: (Number(row.liters) ?? 0) * density,
              weight_unit: 'kg',
            },
          });
        };

        for (let item of data) {
          promises.push(payload(item));
        }

        const allData = await Promise.all(promises);
        question.emissions = allData;
      }
      if (currentQuestion === 4) {
        const question = categories[0]?.questions[4];
        const data = question?.answers;
        if (!data || !data.length) return;
        const activeQuestionType = data[0].question;
        const filteredData = data.filter(
          (row: any) => row.question === activeQuestionType,
        );
        const promises = filteredData.map((row: any) => {
          return this.estimate({
            emission_factor: {
              activity_id: row.gas,
              data_version: '^20',
            },
            parameters: {
              weight: Number(row.recharge) || 0,
              weight_unit: 'kg',
            },
          });
        });

        question.emissions = await Promise.all(promises);
      }

      if (currentQuestion === 6) {
        let co21 = 0;
        let co22 = 0;

        co21 =
          Number(categories[0].questions[5]?.answers?.emission ?? 0) * 1000;
        co22 =
          Number(categories[0].questions[6]?.answers?.emission ?? 0) * 1000;

        categories[0].questions[5].emissions = [
          { constituent_gases: { co2: co21, ch4: 0, n2o: 0, co2e_total: 0 } },
        ];

        categories[0].questions[6].emissions = [
          { constituent_gases: { co2: co22, ch4: 0, n2o: 0, co2e_total: 0 } },
        ];
      }
    }

    // Cat 2 Q1 removed per client request
    // if (currentQuestion === 0 && currentCategory === 1) {
    //   const question = categories[1]?.questions[0];
    //   const data = question?.answers;
    //   if (data == null) { return; }
    //   let promises: any = [];
    //   promises.push(this.estimate({
    //     emission_factor: { activity_id: 'electricity-supply_grid-source_supplier_mix', data_version: '^20' },
    //     parameters: { energy: Number(data.ann) ?? 0, energy_unit: 'kWh' }
    //   }));
    //   const allData = await Promise.all(promises);
    //   question.emissions = allData;
    // }
    if (currentQuestion === 1 && currentCategory === 1) {
      const promises: Promise<any>[] = [];

      const question = categories[1]?.questions[1];
      const data = question?.answers;

      if (!data) {
        return;
      }
      const ELECTRICITY_ACTIVITY_ID_MAP: Record<string, string> = {
        grid: 'electricity-supply_grid-source_production_mix',
        renewable_supplier: 'electricity-supply_grid-source_total_supplier_mix',
        self_generated: 'electricity-supply_grid-source_production_mix',
        imported_other: 'electricity-supply_grid-source_production_mix',
      };

      // Handle both new array format and legacy single-object format
      const entries = Array.isArray(data) ? data : [data];

      for (const entry of entries) {
        const energy = Number(entry.ann) || 0;
        if (!energy) continue;
        const activityId =
          ELECTRICITY_ACTIVITY_ID_MAP[entry.electricitySource] ??
          'electricity-supply_grid-source_production_mix';
        promises.push(
          this.estimate({
            emission_factor: {
              activity_id: activityId,
              data_version: '^20',
              region: 'IT',
              // AIB 2024 residual mix: 0.2352 kgCO2e/kWh → +1.0% vs ISPRA/GSE 2022 (0.233)
              // Ember 2025 (default) gives 0.285 → +22.4% gap
              source: 'AIB',
            },
            parameters: {
              energy,
              energy_unit: 'kWh',
            },
          }),
        );
      }

      const result = await Promise.all(promises);
      question.emissions = result;
    }

    if (currentQuestion === 2 && currentCategory === 1) {
      const question = categories[1]?.questions[2];
      const data = question?.answers;

      if (data == null) {
        return;
      }
      let promises: any = [];
      for (let item of data.data) {
        promises.push(
          this.estimate({
            emission_factor: {
              activity_id: 'heat_and_steam-type_district',
              data_version: '^20',
            },
            parameters: {
              energy: Number(item.energy) ?? 0,
              energy_unit: 'kWh',
            },
          }),
        );
      }
      const allData = await Promise.all(promises);
      question.emissions = allData;
    }

    if (currentCategory === 2) {
      if (currentQuestion === 0) {
        let promises: any = [];
        let payload: any = null;

        payload = async (
          money: number = 0,
          money_unit: string = 'eur',
          activityId: string,
        ) => {
          return await this.estimate({
            emission_factor: {
              activity_id: activityId || '',
              data_version: '^0',
              region: 'IT',
            },
            parameters: {
              money: Number(money) ?? 0,
              money_unit: money_unit || 'eur',
            },
          });
        };

        const question = categories[2].questions[0];
        if (question?.answers?.method == 'spend') {
          const spendData = question.answers.spendData;
          // If spendData is a single object, convert it to an array
          const spendItems = Array.isArray(spendData) ? spendData : [spendData];

          spendItems.forEach((item: any) => {
            if (
              item &&
              item.freightCost &&
              item.currency &&
              (item.transportType == 'Sea' ||
                item.transportType == 'Trasporto marittimo')
            ) {
              promises.push(
                payload(
                  item.freightCost,
                  item.currency.toLowerCase(),
                  'transport_services-type_sea_coastal_water_transportation_services',
                ),
              );
            } else if (
              item &&
              item.freightCost &&
              item.currency &&
              (item.transportType == 'Air' ||
                item.transportType == 'Trasporto aereo')
            ) {
              promises.push(
                payload(
                  item.freightCost,
                  item.currency.toLowerCase(),
                  'transport_services-type_air_transport_services',
                ),
              );
            } else if (
              item &&
              item.freightCost &&
              item.currency &&
              (item.transportType == 'Rail' ||
                item.transportType == 'Trasporto ferroviario')
            ) {
              promises.push(
                payload(
                  item.freightCost,
                  item.currency.toLowerCase(),
                  'transport_services-type_railway_transportation_services',
                ),
              );
            } else if (
              item &&
              item.freightCost &&
              item.currency &&
              (item.transportType == 'Pipelines' ||
                item.transportType == 'Oleodotti / Gasdotti')
            ) {
              promises.push(
                payload(
                  item.freightCost,
                  item.currency.toLowerCase(),
                  'transport_services-type_transportation_services_via_pipelines',
                ),
              );
            } else if (
              item &&
              item.freightCost &&
              item.currency &&
              (item.transportType == 'Inland' ||
                item.transportType == 'Trasporto terrestre (su strada)')
            ) {
              promises.push(
                payload(
                  item.freightCost,
                  item.currency.toLowerCase(),
                  'transport_services-type_other_land_transportation_services',
                ),
              );
            } else if (
              item &&
              item.freightCost &&
              item.currency &&
              (item.transportType == 'Water inland' ||
                item.transportType == 'Trasporto fluviale')
            ) {
              promises.push(
                payload(
                  item.freightCost,
                  item.currency.toLowerCase(),
                  'transport_services-type_route_type_inland_water_transportation_services',
                ),
              );
            }
          });
        } else {
          payload = async (
            activityId: string,
            weight: number = 0,
            weight_unit: string = 'kg',
            distance: number = 0,
            distance_unit: string = 'km',
            region?: string,
          ) => {
            const cleanActivityId = activityId?.trim();

            const payloadData: any = {
              emission_factor: {
                activity_id: cleanActivityId,
                data_version: '^0',
                ...(region ? { region } : {}),
              },
              parameters: {
                weight: Number(weight),
                weight_unit: weight_unit,
                distance: Number(distance),
                distance_unit: distance_unit,
              },
            };
            return await this.estimate(payloadData);
          };

          const refrigeratedMap: Record<string, string> = {
            'freight_vehicle-vehicle_type_van-fuel_source_na-vehicle_weight_lte_3.5t-distance_uplift_included':
              'freight_vehicle-vehicle_type_van_refrigerated-fuel_source_na-vehicle_weight_lte_3.5t-distance_uplift_included',
            'freight_vehicle-vehicle_type_rigid_truck-fuel_source_diesel-vehicle_weight_gt_3.5t_lte_7.5t-distance_basis_sfd':
              'freight_vehicle-vehicle_type_hgv_rigid_refrigerated-fuel_source_diesel_5_percent_biodiesel_blend-vehicle_weight_gt_3.5t_lt_7.5t-distance_uplift_included',
            'freight_vehicle-vehicle_type_rigid_truck-fuel_source_diesel-vehicle_weight_gt_7.5t_lte_12t-distance_basis_sfd':
              'freight_vehicle-vehicle_type_hgv_rigid_refrigerated-fuel_source_diesel_5_percent_biodiesel_blend-vehicle_weight_gt_7.5t_lt_12t-distance_uplift_included',
            'freight_vehicle-vehicle_type_rigid_truck-fuel_source_diesel-vehicle_weight_gt_12t_lte_20t-distance_basis_sfd':
              'freight_vehicle-vehicle_type_hgv_rigid_refrigerated-fuel_source_diesel_5_percent_biodiesel_blend-vehicle_weight_gt_12t_lt_20t-distance_uplift_included',
            'freight_vehicle-vehicle_type_hgv-fuel_source_na-vehicle_weight_gt_20t-distance_uplift_included':
              'freight_vehicle-vehicle_type_hgv_refrigerated-fuel_source_na-vehicle_weight_gt_20t-distance_uplift_included',
          };

          const activityData = question.answers?.activityData;
          const activityItems = Array.isArray(activityData)
            ? activityData
            : [activityData];
          let activityId: string = '';

          activityItems.forEach((item: any) => {
            if (item && item.transportModeGroup === 'road-group') {
              if (typeof item.transportMode === 'string') {
                activityId = item.transportMode;
              } else {
                activityId = item.transportMode.uuid;
              }

              if (item.isRefrigerated && refrigeratedMap[activityId]) {
                activityId = refrigeratedMap[activityId];
              }

              promises.push(
                payload(
                  activityId,
                  item.weight,
                  item.weight_unit,
                  item.distance,
                  item.distance_unit,
                  'EU_S_AMERICA',
                ),
              );
            } else if (item && item.transportModeGroup === 'rail-group') {
              if (typeof item.transportMode === 'string') {
                activityId = item.transportMode;
              } else {
                activityId = item.transportMode.uuid;
              }

              if (item.isRefrigerated && refrigeratedMap[activityId]) {
                activityId = refrigeratedMap[activityId];
              }

              promises.push(
                payload(
                  activityId,
                  item.weight,
                  item.weight_unit,
                  item.distance,
                  item.distance_unit,
                  'EU',
                ),
              );
            } else if (item && item.transportModeGroup === 'sea-group') {
              if (typeof item.transportMode === 'string') {
                activityId = item.transportMode;
              } else {
                activityId = item.transportMode.uuid;
              }

              if (item.isRefrigerated && refrigeratedMap[activityId]) {
                activityId = refrigeratedMap[activityId];
              }

              // FCL: use TEU count with ContainerOverDistance unit (twenty_foot_equivalent × distance)
              // LCL: use weight × distance (WeightOverDistance); if only volume (m³) given, convert via 333 kg/m³
              if (item.transportMode?.isFCL && item.teu > 0) {
                promises.push(
                  this.estimate({
                    emission_factor: {
                      activity_id: activityId,
                      data_version: '^0',
                    },
                    parameters: {
                      twenty_foot_equivalent: Number(item.teu),
                      distance: Number(item.distance),
                      distance_unit: item.distance_unit || 'km',
                    },
                  }),
                );
              } else {
                const seaWeight =
                  item.transportMode?.isLCL &&
                  item.volume > 0 &&
                  (!item.weight || item.weight == 0)
                    ? item.volume * 333
                    : item.weight;
                promises.push(
                  payload(
                    activityId,
                    seaWeight,
                    item.weight_unit || 'kg',
                    item.distance,
                    item.distance_unit,
                  ),
                );
              }
            } else if (item && item.transportModeGroup === 'air-group') {
              if (typeof item.transportMode === 'string') {
                activityId = item.transportMode;
              } else {
                activityId = item.transportMode.uuid;
              }

              if (item.isRefrigerated && refrigeratedMap[activityId]) {
                activityId = refrigeratedMap[activityId];
              }

              promises.push(
                payload(
                  activityId,
                  item.weight,
                  item.weight_unit,
                  item.distance,
                  item.distance_unit,
                ),
              );
            }
          });
        }

        const data = await Promise.all(promises);
        question.emissions = data;
      }

      if (currentQuestion === 1) {
        let promises: any = [];
        let payload: any = null;

        payload = async (
          money: number = 0,
          money_unit: string = 'eur',
          activityId: string,
        ) => {
          return await this.estimate({
            emission_factor: {
              activity_id: activityId || '',
              data_version: '^0',
              region: 'IT',
            },
            parameters: {
              money: Number(money) ?? 0,
              money_unit: money_unit || 'eur',
            },
          });
        };

        const question = categories[2]?.questions[1];
        if (question?.answers?.method == 'spend') {
          const spendData = question?.answers?.spendData;

          // If spendData is a single object, convert it to an array
          const spendItems = Array.isArray(spendData) ? spendData : [spendData];

          spendItems.forEach((item: any) => {
            if (
              item &&
              item.freightCost &&
              item.currency &&
              (item.transportType == 'Sea' ||
                item.transportType == 'Trasporto marittimo')
            ) {
              promises.push(
                payload(
                  item.freightCost,
                  item.currency.toLowerCase(),
                  'transport_services-type_sea_coastal_water_transportation_services',
                ),
              );
            } else if (
              item &&
              item.freightCost &&
              item.currency &&
              (item.transportType == 'Air' ||
                item.transportType == 'Trasporto aereo')
            ) {
              promises.push(
                payload(
                  item.freightCost,
                  item.currency.toLowerCase(),
                  'transport_services-type_air_transport_services',
                ),
              );
            } else if (
              item &&
              item.freightCost &&
              item.currency &&
              (item.transportType == 'Rail' ||
                item.transportType == 'Trasporto ferroviario')
            ) {
              promises.push(
                payload(
                  item.freightCost,
                  item.currency.toLowerCase(),
                  'transport_services-type_railway_transportation_services',
                ),
              );
            } else if (
              item &&
              item.freightCost &&
              item.currency &&
              (item.transportType == 'Pipelines' ||
                item.transportType == 'Oleodotti / Gasdotti')
            ) {
              promises.push(
                payload(
                  item.freightCost,
                  item.currency.toLowerCase(),
                  'transport_services-type_transportation_services_via_pipelines',
                ),
              );
            } else if (
              item &&
              item.freightCost &&
              item.currency &&
              (item.transportType == 'Inland' ||
                item.transportType == 'Trasporto terrestre (su strada)')
            ) {
              promises.push(
                payload(
                  item.freightCost,
                  item.currency.toLowerCase(),
                  'transport_services-type_other_land_transportation_services',
                ),
              );
            } else if (
              item &&
              item.freightCost &&
              item.currency &&
              (item.transportType == 'Water inland' ||
                item.transportType == 'Trasporto fluviale')
            ) {
              promises.push(
                payload(
                  item.freightCost,
                  item.currency.toLowerCase(),
                  'transport_services-type_route_type_inland_water_transportation_services',
                ),
              );
            }
          });
        } else {
          payload = async (
            activityId: string,
            weight: number = 0,
            weight_unit: string = 'kg',
            distance: number = 0,
            distance_unit: string = 'km',
            region?: string,
          ) => {
            const cleanActivityId = activityId?.trim();

            const payloadData: any = {
              emission_factor: {
                activity_id: cleanActivityId,
                data_version: '^0',
                ...(region ? { region } : {}),
              },
              parameters: {
                weight: Number(weight),
                weight_unit: weight_unit,
                distance: Number(distance),
                distance_unit: distance_unit,
              },
            };
            return await this.estimate(payloadData);
          };

          const refrigeratedMap: Record<string, string> = {
            'freight_vehicle-vehicle_type_van-fuel_source_na-vehicle_weight_lte_3.5t-distance_uplift_included':
              'freight_vehicle-vehicle_type_van_refrigerated-fuel_source_na-vehicle_weight_lte_3.5t-distance_uplift_included',
            'freight_vehicle-vehicle_type_rigid_truck-fuel_source_diesel-vehicle_weight_gt_3.5t_lte_7.5t-distance_basis_sfd':
              'freight_vehicle-vehicle_type_hgv_rigid_refrigerated-fuel_source_diesel_5_percent_biodiesel_blend-vehicle_weight_gt_3.5t_lt_7.5t-distance_uplift_included',
            'freight_vehicle-vehicle_type_rigid_truck-fuel_source_diesel-vehicle_weight_gt_7.5t_lte_12t-distance_basis_sfd':
              'freight_vehicle-vehicle_type_hgv_rigid_refrigerated-fuel_source_diesel_5_percent_biodiesel_blend-vehicle_weight_gt_7.5t_lt_12t-distance_uplift_included',
            'freight_vehicle-vehicle_type_rigid_truck-fuel_source_diesel-vehicle_weight_gt_12t_lte_20t-distance_basis_sfd':
              'freight_vehicle-vehicle_type_hgv_rigid_refrigerated-fuel_source_diesel_5_percent_biodiesel_blend-vehicle_weight_gt_12t_lt_20t-distance_uplift_included',
            'freight_vehicle-vehicle_type_hgv-fuel_source_na-vehicle_weight_gt_20t-distance_uplift_included':
              'freight_vehicle-vehicle_type_hgv_refrigerated-fuel_source_na-vehicle_weight_gt_20t-distance_uplift_included',
          };

          let activityId: string = '';
          const activityData = question.answers?.activityData;
          const activityItems = Array.isArray(activityData)
            ? activityData
            : [activityData];

          activityItems.forEach((item: any) => {
            if (item && item.transportModeGroup === 'road-group') {
              if (typeof item.transportMode === 'string') {
                activityId = item.transportMode;
              } else {
                activityId = item.transportMode.uuid;
              }

              if (item.isRefrigerated && refrigeratedMap[activityId]) {
                activityId = refrigeratedMap[activityId];
              }

              promises.push(
                payload(
                  activityId,
                  item.weight,
                  item.weight_unit,
                  item.distance,
                  item.distance_unit,
                  'EU_S_AMERICA',
                ),
              );
            } else if (item && item.transportModeGroup === 'rail-group') {
              if (typeof item.transportMode === 'string') {
                activityId = item.transportMode;
              } else {
                activityId = item.transportMode.uuid;
              }

              if (item.isRefrigerated && refrigeratedMap[activityId]) {
                activityId = refrigeratedMap[activityId];
              }

              promises.push(
                payload(
                  activityId,
                  item.weight,
                  item.weight_unit,
                  item.distance,
                  item.distance_unit,
                  'EU',
                ),
              );
            } else if (item && item.transportModeGroup === 'sea-group') {
              if (typeof item.transportMode === 'string') {
                activityId = item.transportMode;
              } else {
                activityId = item.transportMode.uuid;
              }

              if (item.isRefrigerated && refrigeratedMap[activityId]) {
                activityId = refrigeratedMap[activityId];
              }

              // FCL: use TEU count with ContainerOverDistance unit (twenty_foot_equivalent × distance)
              // LCL: use weight × distance (WeightOverDistance); if only volume (m³) given, convert via 333 kg/m³
              if (item.transportMode?.isFCL && item.teu > 0) {
                promises.push(
                  this.estimate({
                    emission_factor: {
                      activity_id: activityId,
                      data_version: '^0',
                    },
                    parameters: {
                      twenty_foot_equivalent: Number(item.teu),
                      distance: Number(item.distance),
                      distance_unit: item.distance_unit || 'km',
                    },
                  }),
                );
              } else {
                const seaWeight =
                  item.transportMode?.isLCL &&
                  item.volume > 0 &&
                  (!item.weight || item.weight == 0)
                    ? item.volume * 333
                    : item.weight;
                promises.push(
                  payload(
                    activityId,
                    seaWeight,
                    item.weight_unit || 'kg',
                    item.distance,
                    item.distance_unit,
                  ),
                );
              }
            } else if (item && item.transportModeGroup === 'air-group') {
              if (typeof item.transportMode === 'string') {
                activityId = item.transportMode;
              } else {
                activityId = item.transportMode.uuid;
              }

              if (item.isRefrigerated && refrigeratedMap[activityId]) {
                activityId = refrigeratedMap[activityId];
              }

              promises.push(
                payload(
                  activityId,
                  item.weight,
                  item.weight_unit,
                  item.distance,
                  item.distance_unit,
                ),
              );
            }
          });
        }

        const data = await Promise.all(promises);
        question.emissions = data;
      }

      if (currentQuestion === 2) {
        let promises: any[] = [];
        let payload: any = null;

        payload = async (
          activityId: string,
          passengers: number = 0,
          distance: number = 0,
          distance_unit: string = 'km',
        ) => {
          const cleanActivityId = activityId.trim();

          const payloadData = {
            emission_factor: {
              activity_id: cleanActivityId,
              data_version: '^0',
            },
            parameters: {
              passengers: Number(passengers),
              distance: Number(distance),
              distance_unit: distance_unit,
            },
          };

          return await this.estimate(payloadData);
        };

        const question = categories[2]?.questions[2];
        const data = question?.answers;

        // Process Urban & Public Transport (Question 0)
        const urbanTransportData =
          data?.filter((item: any) => item?.question === 0) ?? [];
        for (const item of urbanTransportData) {
          if (!item) continue;

          const employees = parseInt(item.employees) || 0;
          const distance = parseFloat(item.distance) || 0;
          const trips = parseInt(item.trips) || 0;
          const totalDistance = distance * trips;

          switch (item.transport) {
            case 'bus':
              promises.push(
                payload(
                  'passenger_vehicle-vehicle_type_city_bus_urban_gt_250000_inhabitants-fuel_source_na-distance_na-engine_size_na',
                  employees,
                  totalDistance,
                  'km',
                ),
              );
              break;

            case 'subway':
            case 'tram':
              promises.push(
                payload(
                  'passenger_train-route_type_subway_tram_and_trolley_bus_large_urban_area-fuel_source_na',
                  employees,
                  totalDistance,
                  'km',
                ),
              );
              break;

            case 'car':
              promises.push(
                payload(
                  'passenger_vehicle-vehicle_type_car-fuel_source_na-distance_na-engine_size_na',
                  employees,
                  totalDistance,
                  'km',
                ),
              );
              break;

            case 'electric_bus':
              promises.push(
                payload(
                  'passenger_vehicle-vehicle_type_city_bus-fuel_source_bev-distance_na-engine_size_na',
                  employees,
                  totalDistance,
                  'km',
                ),
              );
              break;
          }
        }

        // Process Intercity & Personal Transport (Question 1)
        const intercityTransportData = Array.isArray(data)
          ? data.filter((item: any) => item?.question === 1)
          : [];
        for (const item of intercityTransportData) {
          if (!item) continue;

          const employees = parseInt(item.employees) || 0;
          const trips = parseInt(item.trips) || 0;

          if (item.transport === 'car') {
            promises.push(
              this.travelEstimate({
                origin: { query: item.pointA },
                destination: { query: item.pointB },
                travel_mode: 'car',
                passengers: employees,
                trips: trips,
              }),
            );
          } else if (item.transport === 'train') {
            promises.push(
              this.travelEstimate({
                origin: { query: item.pointA },
                destination: { query: item.pointB },
                travel_mode: 'rail',
                passengers: employees,
                trips: trips,
              }),
            );
          }
        }

        // Process Smart Working & Commuting (Question 2)
        const smartWorkingData = Array.isArray(data)
          ? data.filter((item: any) => item?.question === 2)
          : [];
        for (const item of smartWorkingData) {
          if (!item) continue;

          const employees = parseInt(item.employees) || 0;
          const distance = parseFloat(item.distance) || 0;
          const trips = parseInt(item.trips) || 0;
          const smartWorkingPercentage = parseFloat(item.smartWorking) || 0;
          const effectiveDistance =
            distance * trips * (1 - smartWorkingPercentage / 100);

          switch (item.transport) {
            case 'bus':
              promises.push(
                payload(
                  'passenger_vehicle-vehicle_type_city_bus_urban_gt_250000_inhabitants-fuel_source_na-distance_na-engine_size_na',
                  employees,
                  effectiveDistance,
                  'km',
                ),
              );
              break;

            case 'subway':
            case 'tram':
              promises.push(
                payload(
                  'passenger_train-route_type_subway_tram_and_trolley_bus_large_urban_area-fuel_source_na',
                  employees,
                  effectiveDistance,
                  'km',
                ),
              );
              break;

            case 'car':
              promises.push(
                payload(
                  'passenger_vehicle-vehicle_type_car-fuel_source_na-distance_na-engine_size_na',
                  employees,
                  effectiveDistance,
                  'km',
                ),
              );
              break;
          }
        }

        const allData = await Promise.all(promises);
        // Annualize: trips are per-week, multiply by 52 weeks/year
        const WEEKS_PER_YEAR = 52;
        question.emissions = allData.map((e: any) => {
          const result: any = { ...e, co2e: (e.co2e || 0) * WEEKS_PER_YEAR };
          if (e.constituent_gases) {
            result.constituent_gases = {
              ...e.constituent_gases,
              co2e_total:
                e.constituent_gases.co2e_total != null
                  ? Number(e.constituent_gases.co2e_total) * WEEKS_PER_YEAR
                  : null,
              co2:
                e.constituent_gases.co2 != null
                  ? Number(e.constituent_gases.co2) * WEEKS_PER_YEAR
                  : null,
              ch4:
                e.constituent_gases.ch4 != null
                  ? Number(e.constituent_gases.ch4) * WEEKS_PER_YEAR
                  : null,
              n2o:
                e.constituent_gases.n2o != null
                  ? Number(e.constituent_gases.n2o) * WEEKS_PER_YEAR
                  : null,
            };
          }
          return result;
        });
      }

      if (currentQuestion === 3) {
        const question = categories[2].questions[3];
        const data = asArray(question?.answers);

        question.emissions = [];
        for (const item of data) {
          const promises: Promise<any>[] = [];

          if (item && item.transportMode === 'car') {
            promises.push(
              this.travelEstimate({
                origin: { query: item.departure },
                destination: { query: item.destination },
                travel_mode: 'car',
              }),
            );
          } else if (item && item.transportMode === 'flight') {
            promises.push(
              this.travelEstimate({
                origin: { query: item.departure },
                destination: { query: item.destination },
                travel_mode: 'air',
              }),
            );
          } else if (item && item.transportMode === 'train') {
            promises.push(
              this.travelEstimate({
                origin: { query: item.departure },
                destination: { query: item.destination },
                travel_mode: 'rail',
              }),
            );
          }

          const allData = await Promise.all(promises);
          const frequencyCount = Number(item.frequency || 1);
          // Annualize: frequencyType is 'month' (×12) or 'quarter' (×4)
          const annualMultiplier = item.frequencyType === 'quarter' ? 4 : 12;
          const frequency = frequencyCount * annualMultiplier;

          const multipliedEmissions = allData.map((e) => {
            const result: any = { ...e, co2e: e.co2e * frequency };
            if (e.constituent_gases) {
              result.constituent_gases = {
                ...e.constituent_gases,
                co2e_total:
                  e.constituent_gases.co2e_total != null
                    ? Number(e.constituent_gases.co2e_total) * frequency
                    : null,
                co2:
                  e.constituent_gases.co2 != null
                    ? Number(e.constituent_gases.co2) * frequency
                    : null,
                ch4:
                  e.constituent_gases.ch4 != null
                    ? Number(e.constituent_gases.ch4) * frequency
                    : null,
                n2o:
                  e.constituent_gases.n2o != null
                    ? Number(e.constituent_gases.n2o) * frequency
                    : null,
              };
            }
            return result;
          });

          question.emissions.push(...multipliedEmissions);
        }
      }

      if (currentQuestion === 4) {
        const question = categories[2].questions[4];
        const data = asArray(question?.answers);

        if (!Array.isArray(data)) {
          console.error('Expected an array but got:', data);
          return;
        }

        question.emissions = [];
        for (const item of data) {
          const promises: Promise<any>[] = [];

          if (item && item.transportMode.value === 'car') {
            promises.push(
              this.travelEstimate({
                origin: { query: item.departure },
                destination: { query: item.destination },
                travel_mode: 'car',
              }),
              this.estimate({
                emission_factor: {
                  activity_id: 'accommodation-type_hotel_stay',
                  data_version: '^0',
                  region: 'IT',
                },
                parameters: {
                  number: Number(item.roomNights),
                },
              }),
            );
          } else if (item && item.transportMode.value === 'flight') {
            promises.push(
              this.travelEstimate({
                origin: { query: item.departure },
                destination: { query: item.destination },
                travel_mode: 'air',
              }),
              this.estimate({
                emission_factor: {
                  activity_id: 'accommodation-type_hotel_stay',
                  data_version: '^0',
                  region: 'IT',
                },
                parameters: {
                  number: Number(item.roomNights),
                },
              }),
            );
          } else if (item && item.transportMode.value === 'train') {
            promises.push(
              this.travelEstimate({
                origin: { query: item.departure },
                destination: { query: item.destination },
                travel_mode: 'rail',
              }),
              this.estimate({
                emission_factor: {
                  activity_id: 'accommodation-type_hotel_stay',
                  data_version: '^0',
                  region: 'IT',
                },
                parameters: {
                  number: Number(item.roomNights),
                },
              }),
            );
          }

          const allData = await Promise.all(promises);
          const frequencyCount = Number(item.frequency || 1);
          // Annualize: frequencyType is 'month' (×12) or 'quarter' (×4)
          const annualMultiplier = item.frequencyType === 'quarter' ? 4 : 12;
          const frequency = frequencyCount * annualMultiplier;

          const multipliedEmissions = allData.map((e) => {
            const result: any = { ...e, co2e: e.co2e * frequency };
            if (e.constituent_gases) {
              result.constituent_gases = {
                ...e.constituent_gases,
                co2e_total:
                  e.constituent_gases.co2e_total != null
                    ? Number(e.constituent_gases.co2e_total) * frequency
                    : null,
                co2:
                  e.constituent_gases.co2 != null
                    ? Number(e.constituent_gases.co2) * frequency
                    : null,
                ch4:
                  e.constituent_gases.ch4 != null
                    ? Number(e.constituent_gases.ch4) * frequency
                    : null,
                n2o:
                  e.constituent_gases.n2o != null
                    ? Number(e.constituent_gases.n2o) * frequency
                    : null,
              };
            }
            return result;
          });

          question.emissions.push(...multipliedEmissions);
        }
      }
    }

    if (currentCategory === 3) {
      if (currentQuestion === 0) {
        const promises: Promise<any>[] = [];
        const question = categories[3].questions[0];
        const data = asArray(question?.answers);

        if (!data) {
          return;
        }

        for (const item of data) {
          promises.push(
            this.spendEstimate({
              activity: { activity_id: item.main_category },
              spend_year: Number(item.spend_year),
              spend_region: item.spend_region,
              money: Number(item.money_amount),
              money_unit: item.money_unit.toLowerCase(),
            }),
          );
        }

        const result = await Promise.all(promises);
        question.emissions = result;
      }
      if (currentQuestion === 1) {
        const promises: Promise<any>[] = [];
        const question = categories[3].questions[1];
        const data = asArray(question?.answers);

        if (!data) {
          return;
        }

        for (const item of data) {
          promises.push(
            this.spendEstimate({
              activity: { activity_id: item.main_category },
              spend_year: Number(item.spend_year),
              spend_region: item.spend_region,
              money: Number(item.money_amount),
              money_unit: item.money_unit.toLowerCase(),
            }),
          );
        }

        const result = await Promise.all(promises);
        question.emissions = result;
      }
      if (currentQuestion === 2) {
        const question = categories[3].questions[2];

        const data = asArray(question?.answers);
        if (data == null) {
          return;
        }

        // Correct legacy/wrong activity IDs saved in localStorage
        const ACTIVITY_ID_CORRECTIONS: Record<string, string> = {
          // Paper (old UI used 'paper_and_cardboard' prefix which doesn't exist in Climatiq)
          'waste-type_paper_and_cardboard-disposal_method_landfill':
            'waste-type_paper-disposal_method_landfill',
          'waste-type_paper_and_cardboard-disposal_method_combustion':
            'waste-type_paper-disposal_method_combustion_energy_recovery',
          'waste-type_paper_and_cardboard-disposal_method_composting':
            'waste-type_paper-disposal_method_composting',
          'waste-type_paper_and_cardboard-disposal_method_closed_loop':
            'waste-type_paper-disposal_method_closed_loop',
          'waste-type_paper_and_cardboard-disposal_method_open_loop':
            'waste-type_paper-disposal_method_closed_loop',
          // Metals (open_loop and combustion don't exist for generic metals in Climatiq)
          'waste-type_metals-disposal_method_open_loop':
            'waste-type_metals-disposal_method_closed_loop',
          'waste-type_metals-disposal_method_combustion':
            'waste-type_metals-disposal_method_closed_loop',
          // Wood (open_loop doesn't exist; combustion must be combustion_energy_recovery)
          'waste-type_wood-disposal_method_open_loop':
            'waste-type_wood-disposal_method_closed_loop',
          'waste-type_wood-disposal_method_combustion':
            'waste-type_wood-disposal_method_combustion_energy_recovery',
        };

        question.emissions = [];
        for (const item of data) {
          let promises: any = [];
          if ('mainCategory' in item) {
            // Skip entries with missing activity_id to avoid API errors
            if (!item.material_treatment) continue;
            const activityId =
              ACTIVITY_ID_CORRECTIONS[item.material_treatment] ??
              item.material_treatment;
            promises.push(
              this.estimate({
                emission_factor: {
                  activity_id: activityId,
                  data_version: '^0',
                },
                parameters: {
                  weight: Number(item.quantity),
                  weight_unit: item.unit,
                },
              }),
            );
          } else {
            // Skip wastewater entries with missing currency
            if (!item.currency) continue;
            promises.push(
              this.estimate({
                emission_factor: {
                  activity_id:
                    'waste_management-type_other_waste_for_treatment_waste_water_treatment',
                  data_version: '^0',
                },
                parameters: {
                  money: Number(item.amount),
                  money_unit: item.currency.toLowerCase(),
                },
              }),
            );
          }
          const allData = await Promise.all(promises);
          question.emissions.push(...allData);
        }
      }
      if (currentQuestion === 3) {
        const question = categories[3]?.questions[3];
        const data = question?.answers;
        if (data == null || data.usage === false) {
          return;
        }
        const activityIdMap: Record<string, string> = {
          office: 'real_estate-type_office',
          distribution_warehouse_warm:
            'real_estate-type_distribution_warehouse_warm',
          distribution_warehouse_cold:
            'real_estate-type_distribution_warehouse_cold',
          retail_high_street: 'real_estate-type_retail_high_street',
          hotel: 'real_estate-type_hotel',
          non_residential: 'real_estate-type_non_residential_total',
          retail_shopping_centre: 'real_estate-type_retail_shopping_center',
          healthcare: 'real_estate-type_healthcare',
          leisure_lodging: 'real_estate-type_leisure_lodging',
          retail_warehouse: 'real_estate-type_retail_warehouse',
        };
        if (data.selectedMethod === 'average') {
          question.emissions = [];
          for (const item of data.averageBuildingControls) {
            // Normalize the building type from the item
            const normalizedBuildingType =
              item?.buildingType?.toLowerCase().replace(/[ -]/g, '_') || '';

            // Get the activity ID with fallback to non_residential
            const activityId = activityIdMap[normalizedBuildingType];

            let promises: any = [];
            const isNoEpc = item.epcRating === 'not-provided';
            promises.push(
              this.estimate({
                emission_factor: {
                  activity_id: isNoEpc
                    ? activityId
                    : `${activityId}-epc_rating_${item.epcRating.toLowerCase()}`,
                  data_version: '^0',
                  ...(isNoEpc ? { region: 'IT' } : {}),
                },
                parameters: {
                  area: Number(item.floorArea),
                  area_unit: 'm2',
                },
              }),
            );
            const allData = await Promise.all(promises);
            question.emissions.push(...allData);
          }

          for (const item of data.averageVehicleControls) {
            const activityIdMap: Record<string, string> = {
              Passenger_car_small:
                'passenger_vehicle-vehicle_type_small_car-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_na',
              Passenger_car_medium:
                'passenger_vehicle-vehicle_type_medium_car-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_na',
              Light_van:
                'freight_vehicle-vehicle_type_van-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_lt_3.5t',
              Rigid_truck:
                'delivery_vehicles_and_freight-vehicle_type_hgv_rigid-fuel_source_diesel-vehicle_weight_gt_3.5t_lt_7.5t-percentage_load_50',
              Articulated_truck:
                'freight_vehicle-vehicle_type_hgv_articulated-fuel_source_diesel-vehicle_weight_gt_33t-percentage_load_50',
              Electric_car:
                'passenger_vehicle-vehicle_type_medium_car-fuel_source_bev-engine_size_na-vehicle_age_na-vehicle_weight_na',
              Plug_in_hybrid_car:
                'passenger_vehicle-vehicle_type_medium_car-fuel_source_phev-engine_size_na-vehicle_age_na-vehicle_weight_na',
              Motorcycle_scooter:
                'passenger_vehicle-vehicle_type_small_motorbike-fuel_source_petrol-engine_size_na-vehicle_age_na-vehicle_weight_na',
            };
            let promises: any[] = [];

            // Convert "light-van" to "Light_van"
            const subtypeStr =
              typeof item.subtype === 'string' ? item.subtype : '';

            const formattedSubtype = subtypeStr
              .replace(/-/g, '_') // Replace hyphens with underscores
              .replace(/\b\w/g, (char: string) => char.toUpperCase()); // Capitalize each word

            const activity_id = activityIdMap[formattedSubtype];

            if (!activity_id) {
              console.warn(`No activity ID found for subtype: ${item.subtype}`);
              continue;
            }
            promises.push(
              this.estimate({
                emission_factor: {
                  activity_id: activity_id,
                  data_version: '^0',
                },
                parameters: {
                  distance: Number(item.annualDistance) || 10, // fallback to 10 if invalid
                  distance_unit: 'km',
                },
              }),
            );
            const allData = await Promise.all(promises);
            question.emissions.push(...allData);
          }
        }
        if (data.selectedMethod === 'asset-specific') {
          question.emissions = [];
          for (const item of data.assetSpecificBuildingControls) {
            let promises: any = [];
            for (const energyEntriesData of item.energyEntries) {
              // Convert values from strings to numbers
              const leasedArea = Number(energyEntriesData.leasedArea);
              const totalBuildingArea = Number(
                energyEntriesData.totalBuildingArea,
              );
              const occupancyRate = Number(energyEntriesData.occupancyRate);
              const totalBuildingEnergy = Number(
                energyEntriesData.totalBuildingEnergy,
              );

              // Determine if it's a fugitive emission
              const isUSeT = energyEntriesData.type?.includes('fugitive');

              // Determine energy/weight value based on isSubMetered
              const energyValue = energyEntriesData.isSubMetered
                ? Number(energyEntriesData.amount)
                : (leasedArea / totalBuildingArea) *
                  occupancyRate *
                  totalBuildingEnergy;

              // Build the estimation payload
              const isElectricity =
                energyEntriesData.type?.includes('electricity');
              const payload = {
                emission_factor: {
                  activity_id: energyEntriesData.type,
                  data_version: '^0',
                  ...(isElectricity ? { region: 'IT', source: 'AIB' } : {}),
                },
                parameters: isUSeT
                  ? {
                      weight: energyValue,
                      weight_unit: 't',
                    }
                  : {
                      energy: energyValue,
                      energy_unit: 'kWh',
                    },
              };

              // Push the estimation promise
              promises.push(this.estimate(payload));
            }

            // Await all promises outside the loop
            const allData = await Promise.all(promises);
            question.emissions.push(...allData);
          }
          for (const item of data.assetSpecificVehicleControls) {
            const activityIdMap: Record<string, string> = {
              Passenger_car_small:
                'passenger_vehicle-vehicle_type_small_car-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_na',
              Passenger_car_medium:
                'passenger_vehicle-vehicle_type_medium_car-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_na',
              Light_van:
                'freight_vehicle-vehicle_type_van-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_lt_3.5t',
              Rigid_truck:
                'delivery_vehicles_and_freight-vehicle_type_hgv_rigid-fuel_source_diesel-vehicle_weight_gt_3.5t_lt_7.5t-percentage_load_50',
              Articulated_truck:
                'freight_vehicle-vehicle_type_hgv_articulated-fuel_source_diesel-vehicle_weight_gt_33t-percentage_load_50',
              Electric_car:
                'passenger_vehicle-vehicle_type_medium_car-fuel_source_bev-engine_size_na-vehicle_age_na-vehicle_weight_na',
              Plug_in_hybrid_car:
                'passenger_vehicle-vehicle_type_medium_car-fuel_source_phev-engine_size_na-vehicle_age_na-vehicle_weight_na',
              Motorcycle_scooter:
                'passenger_vehicle-vehicle_type_small_motorbike-fuel_source_petrol-engine_size_na-vehicle_age_na-vehicle_weight_na',
            };
            let promises: any[] = [];

            // Convert "light-van" to "Light_van"
            const formattedSubtype = item.subtype
              .replace(/-/g, '_') // Replace hyphens with underscores
              .replace(/\b\w/g, (char: any) => char.toUpperCase()); // Capitalize each word

            const activity_id = activityIdMap[formattedSubtype];

            if (!activity_id) {
              console.warn(`No activity ID found for subtype: ${item.subtype}`);
              continue;
            }

            promises.push(
              this.estimate({
                emission_factor: {
                  activity_id: activity_id,
                  data_version: '^0',
                },
                parameters: {
                  distance: Number(item.annualDistance) || 10, // fallback to 10 if invalid
                  distance_unit: 'km',
                },
              }),
            );
            const allData = await Promise.all(promises);
            question.emissions.push(...allData);
          }
        }
        if (data.selectedMethod === 'lessor-specific') {
          question.emissions = 0;
          for (const item of data.lessorSpecificControls) {
            const S1 = Number(item.scope1Emissions);
            const S2 = Number(item.scope2Emissions);
            const Q_leased = Number(item.leasedQuantity);
            const Q_total = Number(item.totalQuantity);
            const R = item.occupancyRate ?? 1;
            const emissions = (S1 + S2) * (Q_leased / Q_total) * R;
            question.emissions = (Number(question.emissions) || 0) + emissions;
          }
        }
      }
      if (currentQuestion === 4) {
        const question = categories[3]?.questions[4];
        const data = question?.answers;
        if (data == null) {
          return;
        }
        question.emissions = [];
        for (const item of data) {
          let promises: any = [];
          promises.push(
            this.spendEstimate({
              activity: { activity_id: item.main_category },
              spend_year: Number(item.spend_year),
              spend_region: item.spend_region,
              money: Number(item.money_amount),
              money_unit: item.money_unit.toLowerCase(),
            }),
          );
          const allData = await Promise.all(promises);
          question.emissions.push(...allData);
        }
      }
    }

    // Utility functions (ensure these exist above)
    function generateProductGroupId() {
      return 'pg-' + Math.random().toString(36).substr(2, 9);
    }

    function getInputType(
      unit: string,
      fuelType?: string,
    ): 'weight' | 'volume' | 'energy' {
      const u = (unit || '').trim().toLowerCase();
      const energy = ['mj', 'kwh', 'gj', 'btu'];
      const volume = ['m3', 'nm3', 'l', 'lt', 'liter', 'litre', 'm³'];
      const weight = ['kg', 'g', 'lb', 'ton', 'tonne', 't'];
      if (energy.includes(u)) return 'energy';
      if (volume.includes(u)) return 'volume';
      if (weight.includes(u)) return 'weight';
      const map: any = {
        diesel: 'volume',
        petrol: 'volume',
        lpg: 'volume',
        natural_gas: 'volume',
        coal: 'weight',
        hydrogen: 'energy',
        wood_pellets_bio_100: 'weight',
      };
      const key = (fuelType || '').toLowerCase();
      return map[key] || 'energy';
    }

    function fallback(value: any, ...rest: any[]): any {
      for (const v of [value, ...rest]) {
        if (v !== undefined && v !== null && v !== '') return v;
      }
      return undefined;
    }

    // Main calculation block (drop-in)
    if (currentCategory === 4 && currentQuestion === 0) {
      const question = categories[4]?.questions[0];
      const dataNew = question?.answers;
      if (!dataNew) return;

      let cat5_1_direct_total = 0;
      let cat5_1_indirect_total = 0;
      const allResponse: any[] = [];

      for (const productGroup of dataNew.productGroups || []) {
        // ensure id
        if (!productGroup.product_group_id) {
          productGroup.product_group_id = generateProductGroupId();
        }

        // dedicated accumulators per method
        let m1Total = 0;
        let m2Total = 0;
        let m3Total = 0;
        let m4Total = 0;
        let m5Total = 0;

        // convenience normalized region/year
        const region = productGroup.region ?? undefined;
        const year = productGroup.reportingYear ?? undefined;
        const region_fallback = true;

        // METHOD 1 - Energy-using products (M1)
        if (
          productGroup.use_m1 &&
          Array.isArray(productGroup.m1_methods) &&
          productGroup.m1_methods.length
        ) {
          for (const method of productGroup.m1_methods) {
            try {
              // Fuel component (if provided)
              let E_fuel = 0;
              if (
                method.fuel_type &&
                method.fuel_lifetime_quantity != null &&
                method.fuel_lifetime_quantity !== ''
              ) {
                // activity ids map - keys are lowercased expected values
                const fuelActivityIds: any = {
                  diesel: 'fuel-type_diesel-fuel_use_na',
                  petrol: 'fuel-type_motor_gasoline-fuel_use_na',
                  natural_gas: 'fuel-type_natural_gas-fuel_use_na',
                  lpg: 'fuel-type_lpg-fuel_use_stationary',
                  wood_pellets_bio_100:
                    'fuel-type_wood_pellets_bio_100-fuel_use_na',
                  hydrogen: 'fuel-type_hydrogen-fuel_use_na',
                  coal: 'fuel-type_coal-fuel_use_na',
                };

                const fuelTypeKey = (method.fuel_type || '').toLowerCase();
                const inputType = getInputType(
                  method.fuel_lifetime_unit,
                  fuelTypeKey,
                );
                let parameters: any = {};

                if (inputType === 'weight') {
                  parameters = {
                    weight: Number(method.fuel_lifetime_quantity),
                    weight_unit: method.fuel_lifetime_unit,
                  };
                } else if (inputType === 'volume') {
                  parameters = {
                    volume: Number(method.fuel_lifetime_quantity),
                    volume_unit: method.fuel_lifetime_unit,
                  };
                } else {
                  parameters = {
                    energy: Number(method.fuel_lifetime_quantity),
                    energy_unit: method.fuel_lifetime_unit,
                  };
                }

                // add region/year if available (spec requested)
                // if (region) parameters.region = region;
                // if (reportingYear) parameters.year = reportingYear;

                const activityId = fuelActivityIds[fuelTypeKey];
                if (activityId) {
                  const response = await this.estimate({
                    emission_factor: {
                      activity_id: activityId,
                      data_version: '^0',
                    },
                    parameters,
                  });
                  allResponse.push({
                    productGroupId: productGroup.product_group_id,
                    method: 'm1',
                    type: 'fuel',
                    response,
                  });
                  E_fuel = response.co2e ?? 0;
                } else {
                  // unknown fuel type -> skip or fallback; skip here
                  console.warn('Unknown M1 fuel type:', method.fuel_type);
                }
              }

              // Electricity component
              let E_elec = 0;
              if (
                method.elec_lifetime_kwh != null &&
                method.elec_lifetime_kwh !== ''
              ) {
                const parameters: any = {
                  energy: Number(method.elec_lifetime_kwh),
                  energy_unit: 'kWh',
                };
                // if (region) parameters.region = region;
                // if (reportingYear) parameters.year = reportingYear;
                if (region === 'IT') {
                  const response = await this.estimate({
                    emission_factor: {
                      activity_id:
                        'electricity-supply_grid-source_production_mix',
                      data_version: '^0',
                      region,
                      source: 'AIB',
                    },
                    parameters,
                  });
                  allResponse.push({
                    productGroupId: productGroup.product_group_id,
                    method: 'm1',
                    type: 'elec',
                    response,
                  });
                  E_elec = response.co2e ?? 0;
                } else {
                  const response = await this.estimate({
                    emission_factor: {
                      activity_id: 'electricity-energy_source_grid_mix',
                      data_version: '^0',
                    },
                    parameters,
                  });
                  allResponse.push({
                    productGroupId: productGroup.product_group_id,
                    method: 'm1',
                    type: 'elec',
                    response,
                  });
                  E_elec = response.co2e ?? 0;
                }
              }

              // Refrigerant component - only call API if ref_charge_kg > 0
              let E_refrigerant = 0;
              if (
                method.ref_gas_type &&
                method.ref_charge_kg != null &&
                Number(method.ref_charge_kg) > 0
              ) {
                const gasActivityIds: any = {
                  'hfc-134a': 'fugitive_gas-type_hfc_134a',
                  'r-410a': 'fugitive_gas-type_r410a',
                  'hfc-32': 'fugitive_gas-type_hfc_32',
                  'r-1234yf': 'fugitive_gas-type_r1234yf',
                  'sf₆': 'fugitive_gas-type_sulphur_hexafluoride_sf6',
                  sf6: 'fugitive_gas-type_sulphur_hexafluoride_sf6',
                };

                const gasKey = (method.ref_gas_type || '').toLowerCase();
                const leakedKg =
                  Number(method.ref_charge_kg) *
                  (Number(method.ref_leak_pct || 0) / 100);

                if (leakedKg > 0 && gasActivityIds[gasKey]) {
                  const parameters: any = {
                    weight: leakedKg,
                    weight_unit: 'kg',
                  };
                  // if (region) parameters.region = region;
                  // if (reportingYear) parameters.year = reportingYear;

                  const response = await this.estimate({
                    emission_factor: {
                      activity_id: gasActivityIds[gasKey],
                      data_version: '^0',
                    },
                    parameters,
                  });
                  allResponse.push({
                    productGroupId: productGroup.product_group_id,
                    method: 'm1',
                    type: 'refrigerant',
                    response,
                  });
                  E_refrigerant = response.co2e ?? 0;
                } else {
                  // skip rows with zero leakedKg or unknown gas
                }
              }

              // compute unit and multiply by units sold (always from productGroup.N_sold)
              const E_unit =
                (Number(E_fuel) || 0) +
                (Number(E_elec) || 0) +
                (Number(E_refrigerant) || 0);
              const unitsSold = Number(productGroup.N_sold ?? 0);
              const E_total = unitsSold * E_unit;
              m1Total += E_total;
            } catch (err) {
              console.error('Error in M1 calculation:', err);
            }
          }
        }

        // METHOD 2 - Fuels / feedstocks (M2) - corrected
        if (
          productGroup.use_m2 &&
          Array.isArray(productGroup.m2_methods) &&
          productGroup.m2_methods.length
        ) {
          for (const method of productGroup.m2_methods) {
            try {
              const fuelActivityIds: any = {
                diesel: 'fuel-type_diesel-fuel_use_na',
                petrol: 'fuel-type_motor_gasoline-fuel_use_na',
                natural_gas: 'fuel-type_natural_gas-fuel_use_na',
                lpg: 'fuel-type_lpg-fuel_use_na',
                coal: 'fuel-type_coal-fuel_use_na',
                kerosene: 'fuel-type_kerosene-fuel_use_na',
                heavy_fuel_oil: 'fuel-type_heavy_fuel_oil-fuel_use_na',
                wood_pellets_bio_100:
                  'fuel-type_wood_pellets_bio_100-fuel_use_na',
                hydrogen: 'fuel-type_hydrogen-fuel_use_na',
              };

              const fuelTypeKey = (method.fuel_type || '').trim().toLowerCase();
              const activityId = fuelActivityIds[fuelTypeKey];

              // Skip if required data missing
              if (!activityId) {
                console.warn('Unknown M2 fuel type:', method.fuel_type);
                continue;
              }
              if (method.fuel_qty == null || method.fuel_qty === '') {
                // nothing to compute
                continue;
              }

              // Normalize unit and parameter bucket
              const inputType = getInputType(method.fuel_unit, fuelTypeKey);
              let parameters: any = {};
              if (inputType === 'weight') {
                parameters = {
                  weight: Number(method.fuel_qty),
                  weight_unit: method.fuel_unit,
                };
              } else if (inputType === 'volume') {
                parameters = {
                  volume: Number(method.fuel_qty),
                  volume_unit: method.fuel_unit,
                };
              } else {
                parameters = {
                  energy: Number(method.fuel_qty),
                  energy_unit: method.fuel_unit,
                };
              }

              // include region/year
              // if (region) parameters.region = region;
              // if (reportingYear) parameters.year = reportingYear;

              if (region === 'IT') {
                const response = await this.estimate({
                  emission_factor: {
                    activity_id: activityId,
                    data_version: '^0',
                    region,
                    year,
                    region_fallback,
                  },
                  parameters,
                });

                allResponse.push({
                  productGroupId: productGroup.product_group_id,
                  method: 'm2',
                  response,
                });
                m2Total += Number(response.co2e ?? 0);
              } else {
                const response = await this.estimate({
                  emission_factor: {
                    activity_id: activityId,
                    data_version: '^0',
                  },
                  parameters,
                });

                allResponse.push({
                  productGroupId: productGroup.product_group_id,
                  method: 'm2',
                  response,
                });
                m2Total += Number(response.co2e ?? 0);
              }
            } catch (err) {
              console.error('Error in M2 calculation:', err);
            }
          }
        }

        // METHOD 3 - GHG-containing products (M3) - corrected
        if (
          productGroup.use_m3 &&
          Array.isArray(productGroup.m3_methods) &&
          productGroup.m3_methods.length
        ) {
          for (const method of productGroup.m3_methods) {
            try {
              // Skip if no gas charge or zero
              if (
                method.gas_charge_kg == null ||
                Number(method.gas_charge_kg) === 0
              ) {
                continue;
              }
              const gasActivityIds: any = {
                'hfc-134a': 'fugitive_gas-type_hfc_134a',
                'r-410a': 'fugitive_gas-type_r410a',
                'hfc-32': 'fugitive_gas-type_hfc_32',
                'r-1234yf': 'fugitive_gas-type_r1234yf',
                'sf₆': 'fugitive_gas-type_sulphur_hexafluoride_sf6',
                sf6: 'fugitive_gas-type_sulphur_hexafluoride_sf6',
                'n₂o': 'fugitive_gas-type_n2o',
                n2o: 'fugitive_gas-type_n2o',
              };

              const gasKey = (method.gas_type || '').toLowerCase();
              const activityId = gasActivityIds[gasKey];
              if (!activityId) {
                console.warn('Unknown M3 gas type:', method.gas_type);
                continue;
              }

              const leakedKg =
                (Number(method.gas_charge_kg) *
                  Number(method.gas_leak_pct || 0)) /
                100;
              if (leakedKg <= 0) {
                continue; // nothing to estimate
              }

              const parameters: any = { weight: leakedKg, weight_unit: 'kg' };
              // if (region) parameters.region = region;
              // if (reportingYear) parameters.year = reportingYear;

              const response = await this.estimate({
                emission_factor: {
                  activity_id: activityId,
                  data_version: '^0',
                },
                parameters,
              });

              allResponse.push({
                productGroupId: productGroup.product_group_id,
                method: 'm3',
                response,
              });

              // Multiply per unit sold (units in productGroup.N_sold or method-level override later if added)
              const unitsSold = Number(productGroup.N_sold ?? 0);
              const E_total = unitsSold * Number(response.co2e ?? 0);
              m3Total += E_total;
            } catch (err) {
              console.error('Error in M3 calculation:', err);
            }
          }
        }

        // METHOD 4 — Scenario-based indirect energy use (M4)
        if (
          productGroup.use_m4 &&
          Array.isArray(productGroup.m4_methods) &&
          productGroup.m4_methods.length
        ) {
          for (const method of productGroup.m4_methods) {
            try {
              let E_scn = 0;

              // Determine region/year via fallback chain
              const regionFinal = fallback(
                method.scn_region,
                productGroup.region,
                question.answers?.region,
              );

              const yearFinal = fallback(
                method.scn_year,
                productGroup.reportingYear,
                question.answers?.reportingYear,
              );

              // SCENARIO FUEL COMPONENT
              if (method.scn_fuel_type && method.scn_fuel_qty) {
                const fuelActivityIds: any = {
                  diesel: 'fuel-type_diesel-fuel_use_na',
                  petrol: 'fuel-type_motor_gasoline-fuel_use_na',
                  natural_gas: 'fuel-type_natural_gas-fuel_use_na',
                  lpg: 'fuel-type_lpg-fuel_use_na',
                  coal: 'fuel-type_coal-fuel_use_na',
                  kerosene: 'fuel-type_kerosene-fuel_use_na',
                  heavy_fuel_oil: 'fuel-type_heavy_fuel_oil-fuel_use_na',
                  wood_pellets_bio_100:
                    'fuel-type_wood_pellets_bio_100-fuel_use_na',
                  hydrogen: 'fuel-type_hydrogen-fuel_use_na',
                };

                const fuelKey = (method.scn_fuel_type || '').toLowerCase();
                const activityId = fuelActivityIds[fuelKey];

                if (activityId) {
                  const inputType = getInputType(method.scn_fuel_unit, fuelKey);
                  let parameters: any = {};

                  if (inputType === 'weight') {
                    parameters = {
                      weight: Number(method.scn_fuel_qty),
                      weight_unit: method.scn_fuel_unit,
                    };
                  } else if (inputType === 'volume') {
                    parameters = {
                      volume: Number(method.scn_fuel_qty),
                      volume_unit: method.scn_fuel_unit,
                    };
                  } else {
                    parameters = {
                      energy: Number(method.scn_fuel_qty),
                      energy_unit: method.scn_fuel_unit,
                    };
                  }

                  if (region === 'IT') {
                    const response = await this.estimate({
                      emission_factor: {
                        activity_id: activityId,
                        data_version: '^0',
                        region,
                        year,
                        region_fallback,
                      },
                      parameters,
                    });

                    allResponse.push({
                      group: productGroup.product_group_id,
                      method: 'm4',
                      type: 'fuel',
                      response,
                    });

                    E_scn +=
                      Number(productGroup.U_life || 0) *
                      (Number(method.scn_share_pct || 0) / 100) *
                      Number(productGroup.N_sold || 0) *
                      Number(response.co2e || 0);
                  } else {
                    const response = await this.estimate({
                      emission_factor: {
                        activity_id: activityId,
                        data_version: '^0',
                      },
                      parameters,
                    });

                    allResponse.push({
                      group: productGroup.product_group_id,
                      method: 'm4',
                      type: 'fuel',
                      response,
                    });

                    E_scn +=
                      Number(productGroup.U_life || 0) *
                      (Number(method.scn_share_pct || 0) / 100) *
                      Number(productGroup.N_sold || 0) *
                      Number(response.co2e || 0);
                  }
                }
              }

              // SCENARIO ELECTRICITY
              if (method.scn_elec_kwh != null && method.scn_elec_kwh !== '') {
                let parameters: any = {
                  energy: Number(method.scn_elec_kwh),
                  energy_unit: 'kWh',
                };

                if (region === 'IT') {
                  const response = await this.estimate({
                    emission_factor: {
                      activity_id:
                        'electricity-supply_grid-source_production_mix',
                      data_version: '^0',
                      region,
                      source: 'AIB',
                    },
                    parameters,
                  });

                  allResponse.push({
                    group: productGroup.product_group_id,
                    method: 'm4',
                    type: 'elec',
                    response,
                  });

                  E_scn +=
                    Number(productGroup.U_life || 0) *
                    (Number(method.scn_share_pct || 0) / 100) *
                    Number(productGroup.N_sold || 0) *
                    Number(response.co2e || 0);
                } else {
                  const response = await this.estimate({
                    emission_factor: {
                      activity_id: 'electricity-energy_source_grid_mix',
                      data_version: '^0',
                    },
                    parameters,
                  });

                  allResponse.push({
                    group: productGroup.product_group_id,
                    method: 'm4',
                    type: 'elec',
                    response,
                  });

                  E_scn +=
                    Number(productGroup.U_life || 0) *
                    (Number(method.scn_share_pct || 0) / 100) *
                    Number(productGroup.N_sold || 0) *
                    Number(response.co2e || 0);
                }
              }

              // SCENARIO REFRIGERANT (correct gas type)
              if (
                method.scn_ref_leak_kg != null &&
                Number(method.scn_ref_leak_kg) > 0
              ) {
                const gasActivityIds: any = {
                  'hfc-134a': 'fugitive_gas-type_hfc_134a',
                  'r-410a': 'fugitive_gas-type_r410a',
                  'hfc-32': 'fugitive_gas-type_hfc_32',
                  'r-1234yf': 'fugitive_gas-type_r1234yf',
                  'sf₆': 'fugitive_gas-type_sulphur_hexafluoride_sf6',
                  sf6: 'fugitive_gas-type_sulphur_hexafluoride_sf6',
                };

                const gasKey = (method.scn_ref_gas_type || '').toLowerCase();
                const activityId = gasActivityIds[gasKey];

                if (activityId) {
                  const parameters: any = {
                    weight: Number(method.scn_ref_leak_kg),
                    weight_unit: 'kg',
                  };

                  const response = await this.estimate({
                    emission_factor: {
                      activity_id: activityId,
                      data_version: '^0',
                    },
                    parameters,
                  });

                  allResponse.push({
                    group: productGroup.product_group_id,
                    method: 'm4',
                    type: 'refrigerant',
                    response,
                  });

                  E_scn +=
                    Number(productGroup.U_life || 0) *
                    (Number(method.scn_share_pct || 0) / 100) *
                    Number(productGroup.N_sold || 0) *
                    Number(response.co2e || 0);
                }
              }

              // SCENARIO GHG GAS
              if (method.scn_ghg_kg && method.scn_ghg_type) {
                const gasActivityIds: any = {
                  'hfc-134a': 'fugitive_gas-type_hfc_134a',
                  'r-410a': 'fugitive_gas-type_r410a',
                  'hfc-32': 'fugitive_gas-type_hfc_32',
                  'r-1234yf': 'fugitive_gas-type_r1234yf',
                  'sf₆': 'fugitive_gas-type_sulphur_hexafluoride_sf6',
                  sf6: 'fugitive_gas-type_sulphur_hexafluoride_sf6',
                  'n₂o': 'fugitive_gas-type_n2o',
                  n2o: 'fugitive_gas-type_n2o',
                };

                const gasKey = (method.scn_ghg_type || '').toLowerCase();
                const activityId = gasActivityIds[gasKey];

                if (activityId) {
                  const parameters: any = {
                    weight: Number(method.scn_ghg_kg),
                    weight_unit: 'kg',
                  };

                  const response = await this.estimate({
                    emission_factor: {
                      activity_id: activityId,
                      data_version: '^0',
                    },
                    parameters,
                  });

                  allResponse.push({
                    group: productGroup.product_group_id,
                    method: 'm4',
                    type: 'ghg',
                    response,
                  });

                  E_scn +=
                    Number(productGroup.U_life || 0) *
                    (Number(method.scn_share_pct || 0) / 100) *
                    Number(productGroup.N_sold || 0) *
                    Number(response.co2e || 0);
                }
              }

              m4Total += E_scn;
            } catch (error) {
              console.error('Error in M4:', error);
            }
          }
        }

        // METHOD 5 — Intermediate Products (M5)
        if (productGroup.use_m5) {
          try {
            const qty = Number(productGroup.intermed_sold || 0);
            const unit = (productGroup.intermed_unit || '').toLowerCase();
            const U_life_final = Number(productGroup.U_life_final || 0);
            const EF = Number(productGroup.EF_per_use_intermed || 0);

            // allowed units
            const allowedUnits = ['items', 'item', 'kg', 't', 'l'];
            if (!allowedUnits.includes(unit)) {
              console.warn('Invalid M5 unit:', unit);
              continue;
            }

            // fallback chain for region/year — final version (NO globalRegion/globalYear)
            const regionFinal = fallback(
              productGroup.intermed_region,
              productGroup.region,
              question.answers?.region,
            );

            const yearFinal = fallback(
              productGroup.intermed_year,
              productGroup.reportingYear,
              question.answers?.reportingYear,
            );

            // ITEMS → no API call (pure math)
            if (unit === 'items' || unit === 'item') {
              const E_M5 = qty * U_life_final * EF;
              m5Total += E_M5;
              continue;
            }

            // KG / T / L → same formula as items, using user-supplied EF
            const E_M5 = qty * U_life_final * EF;
            m5Total += E_M5;
          } catch (error) {
            console.error('Error in M5:', error);
          }
        }

        // Final roll-ups for this product group
        const groupDirectTotal = m1Total + m2Total + m3Total;
        const groupIndirectTotal = m4Total + m5Total;

        // update category totals
        cat5_1_direct_total += groupDirectTotal;
        cat5_1_indirect_total += groupIndirectTotal;

        // save per-method results cleanly
        productGroup.calculationResults = {
          m1: productGroup.use_m1 ? m1Total : 0,
          m2: productGroup.use_m2 ? m2Total : 0,
          m3: productGroup.use_m3 ? m3Total : 0,
          m4: productGroup.use_m4 ? m4Total : 0,
          m5: productGroup.use_m5 ? m5Total : 0,
          total: groupDirectTotal + groupIndirectTotal,
        };
      }

      // Store the final computed total (with all multipliers applied) for use in category totals
      question.emissions = [
        { co2e: cat5_1_direct_total + cat5_1_indirect_total, co2e_unit: 'kg' },
      ];
    }

    if (currentCategory === 4 && currentQuestion === 1) {
      const question = categories[4]?.questions[1];
      const data = question?.answers;
      if (data == null || data.usage == false) {
        return;
      }
      const activityIdMap: Record<string, string> = {
        office: 'real_estate-type_office',
        distribution_warehouse_warm:
          'real_estate-type_distribution_warehouse_warm',
        distribution_warehouse_cold:
          'real_estate-type_distribution_warehouse_cold',
        retail_high_street: 'real_estate-type_retail_high_street',
        hotel: 'real_estate-type_hotel',
        non_residential: 'real_estate-type_non_residential_total',
        retail_shopping_centre: 'real_estate-type_retail_shopping_center',
        healthcare: 'real_estate-type_healthcare',
        leisure_lodging: 'real_estate-type_leisure_lodging',
        retail_warehouse: 'real_estate-type_retail_warehouse',
      };
      if (data.selectedMethod === 'average') {
        // Process building emissions
        const buildingPromises = data.averageLeasedBuildingControls.map(
          async (item: any) => {
            // Ensure buildingType is a string
            const buildingTypeStr =
              typeof item.buildingType === 'string' ? item.buildingType : '';
            const normalizedBuildingType = buildingTypeStr
              .toLowerCase()
              .replace(/[ -]/g, '_');

            const activityId = activityIdMap[normalizedBuildingType];
            if (!activityId) {
              console.warn(
                'No activity ID for leased building type:',
                normalizedBuildingType,
              );
              return null;
            }

            // Ensure epcRating is a string
            const epcRatingStr =
              typeof item.epcRating === 'string'
                ? item.epcRating
                : 'not-provided';

            const activityIdWithEpc =
              epcRatingStr === 'not-provided'
                ? activityId
                : `${activityId}-epc_rating_${epcRatingStr.toLowerCase()}`;

            return this.estimate({
              emission_factor: {
                activity_id: activityIdWithEpc,
                data_version: '^0',
              },
              parameters: {
                area: Number(item.floorArea),
                area_unit: 'm2',
              },
            });
          },
        );

        // Process vehicle emissions
        const vehiclePromises = data.averageLeasedOtherAssetControls.map(
          async (item: any) => {
            const activityIdMap: Record<string, string> = {
              passenger_car_small:
                'passenger_vehicle-vehicle_type_small_car-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_na',
              passenger_car_medium:
                'passenger_vehicle-vehicle_type_medium_car-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_na',
              light_van:
                'freight_vehicle-vehicle_type_van-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_lt_3.5t',
              rigid_truck:
                'delivery_vehicles_and_freight-vehicle_type_hgv_rigid-fuel_source_diesel-vehicle_weight_gt_3.5t_lt_7.5t-percentage_load_50',
              articulated_truck:
                'freight_vehicle-vehicle_type_hgv_articulated-fuel_source_diesel-vehicle_weight_gt_33t-percentage_load_50',
              electric_car:
                'passenger_vehicle-vehicle_type_medium_car-fuel_source_bev-engine_size_na-vehicle_age_na-vehicle_weight_na',
              plug_in_hybrid_car:
                'passenger_vehicle-vehicle_type_medium_car-fuel_source_phev-engine_size_na-vehicle_age_na-vehicle_weight_na',
              motorcycle_scooter:
                'passenger_vehicle-vehicle_type_small_motorbike-fuel_source_petrol-engine_size_na-vehicle_age_na-vehicle_weight_na',
            };

            // Normalize the subtype to match map keys
            const subtypeStr =
              typeof item.subtype === 'string' ? item.subtype : '';
            const normalizedSubtype = subtypeStr
              .toLowerCase()
              .replace(/-/g, '_');

            const activity_id = activityIdMap[normalizedSubtype];

            if (!activity_id) {
              console.warn(
                `No activity ID found for vehicle subtype: ${item.subtype}`,
              );
              return null;
            }

            return this.estimate({
              emission_factor: {
                activity_id,
                data_version: '^0',
              },
              parameters: {
                distance: Number(item.annualDistance) || 10,
                distance_unit: 'km',
              },
            });
          },
        );

        // Wait for all promises to resolve
        try {
          const [buildingEmissions, vehicleEmissions] = await Promise.all([
            Promise.all(buildingPromises),
            Promise.all(vehiclePromises),
          ]);

          // Combine and filter results (remove nulls from both if any)
          question.emissions = [
            ...buildingEmissions.filter(Boolean),
            ...vehicleEmissions.filter(Boolean),
          ];
        } catch (error) {
          console.error('Error calculating emissions:', error);
          question.emissions = [];
        }
      }
      if (data.selectedMethod === 'asset-specific') {
        question.emissions = [];
        for (const item of data.assetSpecificLeasedBuildingControls) {
          // Calculate prorate factor based on lease duration
          const leaseStart = new Date(item.leaseStartDate);
          const leaseEnd = new Date(item.leaseEndDate);
          const daysLeased =
            (leaseEnd.getTime() - leaseStart.getTime()) /
              (1000 * 60 * 60 * 24) +
            1;
          const prorateFactor = daysLeased / 365;

          let promises: any = [];

          for (const energyEntriesData of item.entries) {
            // Convert numeric fields
            const leasedArea = Number(energyEntriesData.leasedArea);
            const totalBuildingArea = Number(
              energyEntriesData.totalBuildingArea,
            );
            const occupancyRate = Number(item.occupancyRate ?? 1); // default to 1 if missing
            const totalBuildingEnergy = Number(
              energyEntriesData.totalBuildingEnergy,
            );

            // Determine if it's a fugitive emission type
            const isFugitive = energyEntriesData.type?.includes('fugitive');

            // Determine energy or fuel allocation
            const energyValue = energyEntriesData.isSubMetered
              ? Number(energyEntriesData.amount)
              : (leasedArea / totalBuildingArea) *
                occupancyRate *
                totalBuildingEnergy;

            // Build estimation payload
            const payload = {
              emission_factor: {
                activity_id: energyEntriesData.type,
                data_version: '^0',
              },
              parameters: isFugitive
                ? {
                    weight: energyValue,
                    weight_unit: 't',
                  }
                : {
                    energy: energyValue,
                    energy_unit: 'kWh', // You may need to change this if the unit differs
                  },
            };

            // Push the estimation promise
            promises.push(this.estimate(payload));
          }

          // Await all API estimation calls
          const allData = await Promise.all(promises);

          // Prorate final emissions for lease duration
          question.emissions.push(
            ...allData.map((est) => ({
              ...est,
              co2e: est.co2e * prorateFactor,
              co2e_unit: est.co2e_unit,
            })),
          );
        }

        for (const item of data.assetSpecificLeasedOtherAssetControls) {
          const activityIdMap: Record<string, string> = {
            Passenger_car_small:
              'passenger_vehicle-vehicle_type_small_car-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_na',
            Passenger_car_medium:
              'passenger_vehicle-vehicle_type_medium_car-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_na',
            Light_van:
              'freight_vehicle-vehicle_type_van-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_lt_3.5t',
            Rigid_truck:
              'delivery_vehicles_and_freight-vehicle_type_hgv_rigid-fuel_source_diesel-vehicle_weight_gt_3.5t_lt_7.5t-percentage_load_50',
            Articulated_truck:
              'freight_vehicle-vehicle_type_hgv_articulated-fuel_source_diesel-vehicle_weight_gt_33t-percentage_load_50',
            Electric_car:
              'passenger_vehicle-vehicle_type_medium_car-fuel_source_bev-engine_size_na-vehicle_age_na-vehicle_weight_na',
            Plug_in_hybrid_car:
              'passenger_vehicle-vehicle_type_medium_car-fuel_source_phev-engine_size_na-vehicle_age_na-vehicle_weight_na',
            Motorcycle_scooter:
              'passenger_vehicle-vehicle_type_small_motorbike-fuel_source_petrol-engine_size_na-vehicle_age_na-vehicle_weight_na',
          };
          let promises: any[] = [];

          // Convert "light-van" to "Light_van"
          const formattedSubtype = item.subtype
            .replace(/-/g, '_') // Replace hyphens with underscores
            .replace(/\b\w/g, (char: any) => char.toUpperCase()); // Capitalize each word

          const activity_id = activityIdMap[formattedSubtype];

          if (!activity_id) {
            console.warn(`No activity ID found for subtype: ${item.subtype}`);
            continue;
          }

          promises.push(
            this.estimate({
              emission_factor: {
                activity_id: activity_id,
                data_version: '^0',
              },
              parameters: {
                distance: Number(item.annualDistance) || 10, // fallback to 10 if invalid
                distance_unit: 'km',
              },
            }),
          );

          // Conversion factors (kWh per unit) - moved to constant outside the loop
          const ENERGY_CONVERSION_FACTORS: Record<string, number> = {
            'fuel-type_diesel-fuel_use_na': 10.72,
            'electricity-supply_grid-source_total_supplier_mix': 1,
            'fuel-type_natural_gas-fuel_use_na': 10.83,
            'fuel-type_heating_oil-fuel_use_stationary': 11.22,
          };

          // Initialize variables
          let totalEnergyKWh = 0;

          // Process each energy entry
          item.energyEntries.forEach((entry: any) => {
            // No inner promises variable — push directly to outer `promises`
            const conversionFactor = ENERGY_CONVERSION_FACTORS[entry.fuelType];
            if (conversionFactor === undefined) {
              console.warn(
                `No conversion factor found for fuel type: ${entry.fuelType}`,
              );
              return; // Skip this entry
            }

            // Calculate energy for this entry
            const entryEnergy = parseFloat(entry.amount) * conversionFactor;
            totalEnergyKWh += entryEnergy;

            // Create payload for this specific entry (not total)
            const emissionPayload = {
              emission_factor: {
                activity_id: entry.fuelType, // Using the specific fuel type
                data_version: '^0',
              },
              parameters: {
                energy: entryEnergy, // Using entry-specific energy, not total
                energy_unit: 'kWh',
              },
            };

            promises.push(this.estimate(emissionPayload));
          });

          const allData = await Promise.all(promises);
          question.emissions.push(...allData);
        }
      }
      if (data.selectedMethod === 'lessee-specific') {
        question.emissions = 0;
        for (const item of data.lesseeSpecificControls) {
          const S1 = Number(item.scope1Emissions);
          const S2 = Number(item.scope2Emissions);
          const Q_leased = Number(item.leasedQuantity);
          const Q_total = Number(item.totalQuantity);
          const R = item.occupancyRate ?? 1;
          const leaseStartDate = new Date(item.leaseStartDate);
          const leaseEndDate = new Date(item.leaseEndDate);

          // Normalize both to UTC midnight to ignore time
          const start = new Date(
            Date.UTC(
              leaseStartDate.getUTCFullYear(),
              leaseStartDate.getUTCMonth(),
              leaseStartDate.getUTCDate(),
            ),
          );

          const end = new Date(
            Date.UTC(
              leaseEndDate.getUTCFullYear(),
              leaseEndDate.getUTCMonth(),
              leaseEndDate.getUTCDate(),
            ),
          );

          const oneDay = 1000 * 60 * 60 * 24;
          const daysLeased =
            Math.round((end.getTime() - start.getTime()) / oneDay) + 1;
          const gross_emissions = (S1 + S2) * (Q_leased / Q_total) * R;
          const final_emissions = gross_emissions * (daysLeased / 365);
          categories[4].questions[1].emissions =
            (Number(categories[4].questions[1].emissions) || 0) +
            final_emissions;
        }
      }
    }
    if (currentCategory === 4 && currentQuestion === 2) {
      const question = categories[4]?.questions[2];
      const data = question?.answers;
      if (data == null) {
        return;
      }
      question.emissions = [];
      for (const item of data) {
        const mass_sold = item.mass_sold;

        for (const eolItem of item.eol_methods) {
          const share_pct = eolItem.share_pct;

          // mass_sold is in item.mass_unit (currently always 'kg' from the form)
          const weight = (mass_sold * share_pct) / 100;
          const weight_unit = item.mass_unit || 'kg';

          let promises: any[] = [];
          promises.push(
            this.estimate({
              emission_factor: {
                activity_id: eolItem.waste_type,
                data_version: '^0',
              },
              parameters: {
                weight,
                weight_unit,
              },
            }),
          );

          const allData = await Promise.all(promises);

          question.emissions.push(...allData);
        }
      }
    }
    if (currentCategory === 4 && currentQuestion === 3) {
      const question = categories[4]?.questions[3];
      const data = question?.answers;
      if (data == null) {
        return;
      }
      // Emission factor mapping
      const emissionFactorMap: any = {
        'Listed equity & corporate bonds':
          'financial_services-type_financial_intermediation_services_except_insurance_pension_funding_services',
        'Corporate loans':
          'financial_services-type_services_auxiliary_to_financial_intermediation',
        'Project finance':
          'financial_services-type_financial_intermediation_except_insurance_and_pension_funding-basis_industry',
      };

      // Process all investments
      const portfolioEmissions = await Promise.all(
        data.flatMap((item: any) =>
          item.investments.map(async (investment: any) => {
            // Calculate adjusted investment value
            const inv_adj_eur =
              investment.asset_class ===
              'financial_services-type_financial_intermediation_except_insurance_and_pension_funding-basis_industry'
                ? (investment.inv_value * investment.share_pct) / 100
                : investment.inv_value;

            // Get the correct emission factor based on investment type
            const activity_id =
              emissionFactorMap[investment.type] || investment.asset_class;

            // Get emission factor data
            const emissionData = await this.estimate({
              emission_factor: {
                activity_id,
                data_version: '^0',
              },
              parameters: {
                money: Number(inv_adj_eur),
                money_unit: 'eur', // Changed from usd to eur as per your calculation
              },
            });

            // Calculate E_row (emissions for this investment line)
            const E_row = emissionData.co2e; // kg CO₂e

            return {
              investment,
              emissionData,
              E_row,
              inv_adj_eur,
            };
          }),
        ),
      );
      // Calculate total portfolio emissions (E_port)
      const E_port = portfolioEmissions.reduce(
        (sum, row) => sum + row.E_row,
        0,
      );
      // Store the scalar total so calculateCategoryTotals can read it
      question.emissions = [{ co2e: E_port, co2e_unit: 'kg' }];
    }
    if (currentCategory === 5 && currentQuestion === 0) {
      const question = categories[5]?.questions[0];
      const data = question?.answers;
      if (data == null) {
        return;
      }

      const promises: Promise<any>[] = [];

      for (const item of data) {
        if (!item.custom_factor) {
          const parameters: Record<string, any> = {
            [item.unit_type]: Number(item.activity_value),
          };

          if (item.unit_type !== 'number') {
            parameters[`${item.unit_type}_unit`] = item.activity_unit;
          }

          promises.push(
            this.suggestData({
              suggestion_id: item.suggestion_id,
              parameters,
            }),
          );
        }
      }

      try {
        const results = await Promise.all(promises);
        question.emissions = data.map((item: any) => {
          if (item.custom_factor) {
            return item.activity_value * item.custom_value;
          }
          return results.shift();
        });
      } catch (error) {
        console.error('Error in suggestData calls:', error);
      }
    }
  };
  emissionFactor = async (currentCategory: any, currentQuestion: any) => {
    let data = null;
    let factor = null;
    switch (currentCategory) {
      case 0:
        switch (currentQuestion) {
          case 0:
            data = await this.estimate({
              emission_factor: {
                activity_id: 'fuel-type_natural_gas-fuel_use_na',
                data_version: '^0',
                region: 'DE',
              },
              parameters: {
                energy: Number(0),
                energy_unit: 'kWh',
              },
            });
            factor = data.emission_factor;
            break;

          case 1:
            data = await this.estimate({
              emission_factor: {
                activity_id: 'fuel-type_motor_gasoline-fuel_use_na',
                data_version: '^20',
              },
              parameters: {
                weight: Number(0) ?? 0,
                weight_unit: 'kg',
              },
            });
            factor = data.emission_factor;
            break;

          case 4:
            data = await this.estimate({
              emission_factor: {
                activity_id: 'fugitive_gas-type_hfc_134a',
                data_version: '^20',
              },
              parameters: {
                weight: Number(0) ?? 0,
                weight_unit: 'kg',
              },
            });
            factor = data.emission_factor;
            break;

          case 5:
            factor = null;
            break;

          default:
            factor = null;
        }
        break;

      case 1:
        switch (currentQuestion) {
          case 0:
          case 1:
          case 2:
            data = await this.estimate({
              emission_factor: {
                activity_id: 'electricity-supply_grid-source_production_mix',
                data_version: '^20',
                region: 'IT',
                source: 'AIB',
              },
              parameters: {
                energy: Number(0),
                energy_unit: 'kWh',
              },
            });
            factor = data.emission_factor;
            break;

          default:
            factor = null;
        }
        break;

      case 2:
        switch (currentQuestion) {
          case 0:
          case 1:
            // Transportation - Freight (spend-based)
            data = await this.estimate({
              emission_factor: {
                activity_id:
                  'transport_services-type_sea_coastal_water_transportation_services',
                data_version: '^0',
              },
              parameters: {
                money: Number(0) ?? 0,
                money_unit: 'eur',
              },
            });
            factor = data.emission_factor;
            break;

          case 2:
            // Employee commuting
            data = await this.estimate({
              emission_factor: {
                activity_id:
                  'passenger_vehicle-vehicle_type_city_bus_urban_gt_250000_inhabitants-fuel_source_na-distance_na-engine_size_na',
                data_version: '^0',
              },
              parameters: {
                passengers: Number(0),
                distance: Number(0),
                distance_unit: 'km',
              },
            });
            factor = data.emission_factor;
            break;

          case 3:
          case 4:
            // Business travel - uses travelEstimate, no direct emission factor
            factor = null;
            break;

          default:
            factor = null;
        }
        break;

      case 3:
        switch (currentQuestion) {
          case 0:
          case 1:
          case 4:
            // Purchased goods/services, Capital goods, Investments - spend-based
            // Using spendEstimate method, no direct emission factor
            factor = null;
            break;

          case 2:
            // Waste generated
            data = await this.estimate({
              emission_factor: {
                activity_id:
                  'waste_management-type_other_waste_for_treatment_waste_water_treatment',
                data_version: '^0',
              },
              parameters: {
                money: Number(0),
                money_unit: 'eur',
              },
            });
            factor = data.emission_factor;
            break;

          case 3:
            // Leased assets (upstream) - average method
            data = await this.estimate({
              emission_factor: {
                activity_id: 'real_estate-type_office',
                data_version: '^0',
              },
              parameters: {
                number: Number(0),
              },
            });
            factor = data.emission_factor;
            break;

          default:
            factor = null;
        }
        break;

      case 4:
        switch (currentQuestion) {
          case 0:
            // Use of sold products - energy-using products
            data = await this.estimate({
              emission_factor: {
                activity_id: 'fuel-type_diesel-fuel_use_na',
                data_version: '^0',
              },
              parameters: {
                energy: Number(0),
                energy_unit: 'kWh',
              },
            });
            factor = data.emission_factor;
            break;

          case 1:
            // Leased assets (downstream)
            data = await this.estimate({
              emission_factor: {
                activity_id: 'real_estate-type_office',
                data_version: '^0',
              },
              parameters: {
                number: Number(0),
              },
            });
            factor = data.emission_factor;
            break;

          case 2:
            // End-of-life treatment
            data = await this.estimate({
              emission_factor: {
                activity_id:
                  'waste_management-type_other_waste_for_treatment_waste_water_treatment',
                data_version: '^0',
              },
              parameters: {
                weight: Number(0),
                weight_unit: 'kg',
              },
            });
            factor = data.emission_factor;
            break;

          case 3:
            // Investments
            data = await this.estimate({
              emission_factor: {
                activity_id:
                  'financial_services-type_financial_intermediation_services_except_insurance_pension_funding_services',
                data_version: '^0',
              },
              parameters: {
                money: Number(0),
                money_unit: 'eur',
              },
            });
            factor = data.emission_factor;
            break;

          default:
            factor = null;
        }
        break;

      case 5:
        switch (currentQuestion) {
          case 0:
            // Custom emissions - uses suggestData method
            factor = null;
            break;

          default:
            factor = null;
        }
        break;

      default:
        factor = null;
        break;
    }

    return factor;
  };
  getSuggestions(searchText: string): Observable<any> {
    const payload = {
      suggest: {
        text: searchText,
      },
      max_suggestions: 5,
    };

    const promise = this.proxyPost('suggest', payload).catch((error: any) => {
      console.error('Climatiq API error:', error);
      return { suggestions: [] };
    });

    return from(promise);
  }
  translateText(
    text: string,
    source_lang: string = '',
    target_lang: string,
  ): Promise<DeepLTranslationResponse> {
    return this.deeplAxios
      .post('', {
        text,
        target_lang,
        source_lang: source_lang || undefined,
      })
      .then((response) => response.data)
      .catch((error) => {
        console.error(
          'AWS Proxy Error:',
          error.response?.data || error.message,
        );
        throw error;
      });
  }
}
